import React, { useMemo } from 'react';
import {
  Car,
  RefreshCw,
  Target,
  DollarSign,
  TrendingUp,
  Users,
  Calendar,
  MessageSquare,
  Activity,
  BarChart3,
  ArrowUpRight,
  Download
} from 'lucide-react';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getCustomerName = (customer = {}) => {
  const name = customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
  return name || 'Unknown Customer';
};

const isAcquired = (customer = {}) => {
  const status = (customer.acquisitionStatus || '').toLowerCase();
  const genericStatus = (customer.status || '').toLowerCase();
  return ['acquired', 'completed', 'closed-won', 'sold'].some(
    (state) => status === state || genericStatus === state
  );
};

const parseNumericOffer = (value) => {
  if (!value || typeof value === 'number') return value || 0;
  const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const buildVehicleLabel = (customer = {}) => {
  const { vehicleYear, vehicleMake, vehicleModel, vehicle } = customer;
  if (vehicle) return vehicle;
  return [vehicleYear, vehicleMake, vehicleModel].filter(Boolean).join(' ') || 'Vehicle not specified';
};

const formatRelativeTime = (dateInput) => {
  if (!dateInput) return 'Unknown time';
  const now = Date.now();
  const target = new Date(dateInput).getTime();
  if (Number.isNaN(target)) return 'Unknown time';
  const diffMinutes = Math.max(0, Math.round((now - target) / 60000));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  const diffWeeks = Math.round(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  const diffYears = Math.round(diffDays / 365);
  return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
};

const getRangeLabel = (range) => {
  switch (range) {
    case '7days':
      return 'Last 7 Days';
    case '30days':
      return 'Last 30 Days';
    case '90days':
      return 'Last 90 Days';
    default:
      return 'All Time';
  }
};

export const AnalyticsTab = ({ customers = [], theme, colors, dateRange, setDateRange, showToast }) => {
  const rangeLabel = useMemo(() => getRangeLabel(dateRange), [dateRange]);

  const filteredCustomers = useMemo(() => {
    if (!Array.isArray(customers) || customers.length === 0) return [];

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    let threshold = null;
    switch (dateRange) {
      case '7days': {
        threshold = new Date(now);
        threshold.setDate(now.getDate() - 6);
        break;
      }
      case '30days': {
        threshold = new Date(now);
        threshold.setDate(now.getDate() - 29);
        break;
      }
      case '90days': {
        threshold = new Date(now);
        threshold.setDate(now.getDate() - 89);
        break;
      }
      default:
        threshold = null;
    }

    if (!threshold) {
      return [...customers];
    }

    threshold.setHours(0, 0, 0, 0);

    return customers.filter((customer) => {
      if (!customer?.createdAt) return false;
      const created = new Date(customer.createdAt);
      if (Number.isNaN(created.getTime())) return false;
      created.setHours(0, 0, 0, 0);
      return created >= threshold;
    });
  }, [customers, dateRange]);

  const metrics = useMemo(() => {
    const list = filteredCustomers;
    const totalCustomers = list.length;

    const activeConversations = list.filter(
      (customer) =>
        Array.isArray(customer?.conversations) &&
        customer.conversations.length > 0 &&
        (customer.acquisitionStatus || '').toLowerCase() !== 'lost-declined'
    ).length;

    const appointmentsSet = list.filter(
      (customer) => (customer.acquisitionStatus || '').toLowerCase() === 'appointment-set'
    ).length;

    const hotOpportunities = list.filter(
      (customer) => (customer.acquisitionStatus || '').toLowerCase() === 'hot-opportunity' || customer.tradeInInterest
    ).length;

    const contacted = list.filter(
      (customer) => Array.isArray(customer?.conversations) && customer.conversations.length > 0
    ).length;

    const responded = list.filter(
      (customer) =>
        Array.isArray(customer?.conversations) &&
        customer.conversations.some((message) => message?.sender === 'customer')
    ).length;

    const responseRate = contacted > 0 ? Number(((responded / contacted) * 100).toFixed(1)) : 0;

    const responseTimes = [];
    list.forEach((customer) => {
      if (!Array.isArray(customer?.conversations)) return;
      for (let i = 0; i < customer.conversations.length - 1; i++) {
        const current = customer.conversations[i];
        const next = customer.conversations[i + 1];
        if (
          current?.sender === 'ai' &&
          next?.sender === 'customer' &&
          current.timestamp &&
          next.timestamp
        ) {
          const aiTime = new Date(current.timestamp).getTime();
          const customerTime = new Date(next.timestamp).getTime();
          if (!Number.isNaN(aiTime) && !Number.isNaN(customerTime) && customerTime >= aiTime) {
            responseTimes.push((customerTime - aiTime) / 60000);
          }
        }
      }
    });

    const avgResponseTime = responseTimes.length
      ? Math.round(responseTimes.reduce((total, value) => total + value, 0) / responseTimes.length)
      : 0;

    const conversionRate = totalCustomers > 0
      ? Number(((appointmentsSet / totalCustomers) * 100).toFixed(1))
      : 0;

    return {
      totalCustomers,
      activeConversations,
      appointmentsSet,
      hotOpportunities,
      responseRate,
      avgResponseTime,
      conversionRate
    };
  }, [filteredCustomers]);

  const tradeInTotals = useMemo(() => {
    const totalTradeIns = filteredCustomers.reduce(
      (sum, customer) => sum + (Array.isArray(customer?.tradeIns) ? customer.tradeIns.length : 0),
      0
    );
    const customersWithTradeIns = filteredCustomers.filter(
      (customer) => Array.isArray(customer?.tradeIns) && customer.tradeIns.length > 0
    ).length;

    const tradeCaptureRate = filteredCustomers.length > 0
      ? Number(((customersWithTradeIns / filteredCustomers.length) * 100).toFixed(1))
      : 0;

    const estimatedGross = totalTradeIns * 1200;

    return {
      totalTradeIns,
      customersWithTradeIns,
      tradeCaptureRate,
      estimatedGross
    };
  }, [filteredCustomers]);

  const financialStats = useMemo(() => {
    const acquisitions = filteredCustomers.filter(isAcquired);
    const acquisitionCount = acquisitions.length;

    const offerValues = acquisitions
      .map((customer) => parseNumericOffer(customer.currentOffer || customer.offerValue))
      .filter((value) => value > 0);

    const totalOffers = offerValues.reduce((sum, value) => sum + value, 0);
    const averageOffer = offerValues.length ? totalOffers / offerValues.length : 0;
    const acquisitionRevenue = totalOffers || acquisitionCount * 25000;
    const acquisitionProfit = acquisitionCount * 4300;

    return {
      acquisitionCount,
      totalOffers,
      averageOffer,
      acquisitionRevenue,
      acquisitionProfit
    };
  }, [filteredCustomers]);

  const dailyTrends = useMemo(() => {
    const map = new Map();

    filteredCustomers.forEach((customer) => {
      if (!customer?.createdAt) return;
      const created = new Date(customer.createdAt);
      if (Number.isNaN(created.getTime())) return;
      const key = created.toISOString().split('T')[0];
      if (!map.has(key)) {
        map.set(key, { customers: 0, appointments: 0, acquisitions: 0, offerTotal: 0 });
      }
      const bucket = map.get(key);
      bucket.customers += 1;
      if ((customer.acquisitionStatus || '').toLowerCase() === 'appointment-set') {
        bucket.appointments += 1;
      }
      if (isAcquired(customer)) {
        bucket.acquisitions += 1;
      }
      bucket.offerTotal += parseNumericOffer(customer.currentOffer || customer.offerValue);
    });

    const results = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysToShow = 7;

    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const key = date.toISOString().split('T')[0];
      const bucket = map.get(key) || { customers: 0, appointments: 0, acquisitions: 0, offerTotal: 0 };
      results.push({
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        date: key,
        ...bucket
      });
    }

    return results;
  }, [filteredCustomers]);

  const leadSources = useMemo(() => {
    const counts = {};
    filteredCustomers.forEach((customer) => {
      const source = customer.source || 'Unknown';
      counts[source] = (counts[source] || 0) + 1;
    });

    const palette = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#22d3ee', '#f97316'];

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([source, count], index) => ({
        source,
        count,
        percentage: filteredCustomers.length > 0
          ? Number(((count / filteredCustomers.length) * 100).toFixed(1))
          : 0,
        color: palette[index % palette.length]
      }));
  }, [filteredCustomers]);

  const teamPerformance = useMemo(() => {
    const statsMap = new Map();

    filteredCustomers.forEach((customer) => {
      const owner = customer.assignedTo || customer.assignedUser || 'Unassigned';
      if (!statsMap.has(owner)) {
        statsMap.set(owner, { leads: 0, appointments: 0, acquisitions: 0 });
      }
      const stats = statsMap.get(owner);
      stats.leads += 1;
      if ((customer.acquisitionStatus || '').toLowerCase() === 'appointment-set') {
        stats.appointments += 1;
      }
      if (isAcquired(customer)) {
        stats.acquisitions += 1;
      }
    });

    return Array.from(statsMap.entries())
      .map(([name, stats]) => ({
        name,
        role: name === 'Unassigned' ? 'Unassigned' : 'Advisor',
        leads: stats.leads,
        appointments: stats.appointments,
        purchases: stats.acquisitions,
        conversion: stats.leads > 0 ? Number(((stats.acquisitions / stats.leads) * 100).toFixed(1)) : 0,
        status: stats.appointments > 0 ? 'Active' : 'Idle'
      }))
      .sort((a, b) => b.leads - a.leads);
  }, [filteredCustomers]);

  const recentActivity = useMemo(() => {
    const events = [];

    filteredCustomers.forEach((customer) => {
      const name = getCustomerName(customer);
      const vehicle = buildVehicleLabel(customer);

      if (customer.createdAt) {
        events.push({
          type: 'lead',
          action: 'New customer added',
          customer: name,
          vehicle,
          timestamp: customer.createdAt
        });
      }

      if ((customer.acquisitionStatus || '').toLowerCase() === 'appointment-set' && customer.appointmentDate) {
        const combined = customer.appointmentTime
          ? `${customer.appointmentDate} ${customer.appointmentTime}`
          : customer.appointmentDate;
        events.push({
          type: 'appointment',
          action: 'Appointment scheduled',
          customer: name,
          vehicle,
          timestamp: combined
        });
      }

      if (customer.tradeInInterest || (Array.isArray(customer.tradeIns) && customer.tradeIns.length > 0)) {
        events.push({
          type: 'trade-in',
          action: 'Trade-in interest captured',
          customer: name,
          vehicle,
          timestamp: customer.updatedAt || customer.createdAt,
          details: `${customer.tradeIns?.length || 0} trade-ins`
        });
      }

      if (isAcquired(customer)) {
        events.push({
          type: 'acquisition',
          action: 'Vehicle acquired',
          customer: name,
          vehicle,
          timestamp: customer.updatedAt || customer.appointmentDate || customer.createdAt,
          amount: parseNumericOffer(customer.currentOffer || customer.offerValue)
        });
      }

      if (Array.isArray(customer.conversations)) {
        const latestCustomerMessage = [...customer.conversations]
          .reverse()
          .find((message) => message?.sender === 'customer');
        if (latestCustomerMessage?.timestamp) {
          events.push({
            type: 'conversation',
            action: 'Customer replied',
            customer: name,
            vehicle,
            timestamp: latestCustomerMessage.timestamp,
            messagePreview: latestCustomerMessage.message || latestCustomerMessage.content || ''
          });
        }
      }
    });

    return events
      .filter((event) => event.timestamp && !Number.isNaN(new Date(event.timestamp).getTime()))
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 20);
  }, [filteredCustomers]);

  const pipelineStages = useMemo(() => {
    const engaged = filteredCustomers.filter(
      (customer) => Array.isArray(customer?.conversations) && customer.conversations.length > 0
    ).length;

    const offerSent = filteredCustomers.filter(
      (customer) =>
        (customer.acquisitionStatus || '').toLowerCase() === 'offer-sent' ||
        customer.conversations?.some((message) => message?.sender === 'ai')
    ).length;

    const acquired = filteredCustomers.filter(isAcquired).length;

    return [
      {
        label: 'Total Opportunities',
        value: metrics.totalCustomers,
        color: '#3b82f6',
        description: rangeLabel
      },
      {
        label: 'Engaged',
        value: engaged,
        color: '#8b5cf6',
        description: 'Customers with conversations'
      },
      {
        label: 'Offer Sent',
        value: offerSent,
        color: '#f59e0b',
        description: 'Offers delivered'
      },
      {
        label: 'Hot Opportunities',
        value: metrics.hotOpportunities,
        color: '#ef4444',
        description: 'High intent prospects'
      },
      {
        label: 'Acquired',
        value: acquired,
        color: '#10b981',
        description: 'Closed deals'
      }
    ];
  }, [filteredCustomers, metrics, rangeLabel]);

  const aiPerformanceMetrics = useMemo(() => {
    const engaged = filteredCustomers.filter(
      (customer) => Array.isArray(customer?.conversations) && customer.conversations.length > 0
    );

    const responded = engaged.filter((customer) =>
      customer.conversations.some((message) => message?.sender === 'customer')
    );

    const aiMessages = engaged.reduce(
      (sum, customer) =>
        sum + customer.conversations.filter((message) => message?.sender === 'ai').length,
      0
    );

    const manualMessages = engaged.reduce(
      (sum, customer) =>
        sum + customer.conversations.filter((message) => message?.sender === 'user').length,
      0
    );

    const paused = filteredCustomers.filter((customer) => customer.aiPaused || customer.manualControl).length;

    return [
      {
        title: 'Response Rate',
        value: `${metrics.responseRate}%`,
        description: `${responded.length} of ${engaged.length} responded`,
        color: '#3b82f6'
      },
      {
        title: 'AI Messages Sent',
        value: aiMessages.toLocaleString(),
        description: 'Across all conversations',
        color: '#8b5cf6'
      },
      {
        title: 'Manual Messages',
        value: manualMessages.toLocaleString(),
        description: 'Sent by your team',
        color: '#10b981'
      },
      {
        title: 'Paused Conversations',
        value: paused.toLocaleString(),
        description: 'Under manual control',
        color: '#ef4444'
      }
    ];
  }, [filteredCustomers, metrics.responseRate]);

  const metricCards = useMemo(() => [
    {
      title: 'Total Customers',
      value: metrics.totalCustomers.toLocaleString(),
      subtitle: 'In selected range',
      icon: Users,
      iconColor: colors.primary,
      color: '#3b82f6',
      detail: `${metrics.totalCustomers} customers tracked`,
      trend: rangeLabel
    },
    {
      title: 'Active Conversations',
      value: metrics.activeConversations.toLocaleString(),
      subtitle: 'Currently in progress',
      icon: MessageSquare,
      iconColor: colors.success,
      color: '#10b981',
      detail: `${metrics.activeConversations} engaged conversations`,
      trend: rangeLabel
    },
    {
      title: 'Appointments Set',
      value: metrics.appointmentsSet.toLocaleString(),
      subtitle: 'Confirmed appraisals',
      icon: Calendar,
      iconColor: '#8b5cf6',
      color: '#8b5cf6',
      detail: `${metrics.appointmentsSet} scheduled appointments`,
      trend: rangeLabel
    },
    {
      title: 'Hot Opportunities',
      value: metrics.hotOpportunities.toLocaleString(),
      subtitle: 'High intent leads',
      icon: TrendingUp,
      iconColor: '#ef4444',
      color: '#ef4444',
      detail: `${metrics.hotOpportunities} ready to close`,
      trend: rangeLabel
    },
    {
      title: 'Response Rate',
      value: `${metrics.responseRate}%`,
      subtitle: 'Customers who replied',
      icon: Target,
      iconColor: colors.primary,
      color: '#2563eb',
      detail: `${metrics.responseRate}% responded`,
      trend: rangeLabel
    },
    {
      title: 'Avg Response Time',
      value: metrics.avgResponseTime > 0 ? `${metrics.avgResponseTime} min` : '--',
      subtitle: 'AI to customer reply',
      icon: RefreshCw,
      iconColor: '#0ea5e9',
      color: '#0ea5e9',
      detail: metrics.avgResponseTime > 0 ? `Typical reply in ${metrics.avgResponseTime} minutes` : 'No replies yet',
      trend: rangeLabel
    },
    {
      title: 'Conversion Rate',
      value: `${metrics.conversionRate}%`,
      subtitle: 'To appointment-set',
      icon: DollarSign,
      iconColor: '#f59e0b',
      color: '#f59e0b',
      detail: `${metrics.appointmentsSet} of ${metrics.totalCustomers} booked`,
      trend: rangeLabel
    }
  ], [metrics, colors.primary, colors.success, rangeLabel]);

  const hasTrendData = dailyTrends.some(
    (day) => day.customers > 0 || day.appointments > 0 || day.acquisitions > 0
  );

  const handleExportAnalytics = () => {
    try {
      const lines = [];
      lines.push('VinCentrix Analytics Report');
      lines.push(`Range,${rangeLabel}`);
      lines.push(`Generated,${new Date().toISOString()}`);
      lines.push('');

      lines.push('METRIC,VALUE');
      lines.push(`Total Customers,${metrics.totalCustomers}`);
      lines.push(`Active Conversations,${metrics.activeConversations}`);
      lines.push(`Appointments Set,${metrics.appointmentsSet}`);
      lines.push(`Hot Opportunities,${metrics.hotOpportunities}`);
      lines.push(`Response Rate,${metrics.responseRate}%`);
      lines.push(`Average Response Time,${metrics.avgResponseTime} minutes`);
      lines.push(`Conversion Rate,${metrics.conversionRate}%`);
      lines.push('');

      lines.push('TRADE-IN METRICS,VALUE');
      lines.push(`Total Trade-Ins,${tradeInTotals.totalTradeIns}`);
      lines.push(`Customers With Trade-Ins,${tradeInTotals.customersWithTradeIns}`);
      lines.push(`Trade Capture Rate,${tradeInTotals.tradeCaptureRate}%`);
      lines.push(`Estimated Trade-In Gross,$${tradeInTotals.estimatedGross.toLocaleString()}`);
      lines.push('');

      lines.push('FINANCIAL METRICS,VALUE');
      lines.push(`Vehicles Acquired,${financialStats.acquisitionCount}`);
      lines.push(`Total Offer Value,$${Math.round(financialStats.totalOffers).toLocaleString()}`);
      lines.push(`Average Offer,$${Math.round(financialStats.averageOffer).toLocaleString()}`);
      lines.push(`Estimated Acquisition Revenue,$${Math.round(financialStats.acquisitionRevenue).toLocaleString()}`);
      lines.push(`Estimated Acquisition Profit,$${Math.round(financialStats.acquisitionProfit).toLocaleString()}`);
      lines.push('');

      lines.push('LEAD SOURCE,COUNT,PERCENTAGE');
      leadSources.forEach((item) => {
        lines.push(`${item.source},${item.count},${item.percentage}%`);
      });
      lines.push('');

      lines.push('DAILY TRENDS DATE,CUSTOMERS,APPOINTMENTS,ACQUISITIONS,ESTIMATED OFFER VALUE');
      dailyTrends.forEach((day) => {
        lines.push(
          `${day.date},${day.customers},${day.appointments},${day.acquisitions},$${Math.round(day.offerTotal).toLocaleString()}`
        );
      });
      lines.push('');

      lines.push('TEAM MEMBER,ROLE,LEADS,APPOINTMENTS,ACQUISITIONS,CONVERSION');
      teamPerformance.forEach((member) => {
        lines.push(
          `${member.name},${member.role},${member.leads},${member.appointments},${member.purchases},${member.conversion}%`
        );
      });
      lines.push('');

      lines.push('RECENT ACTIVITY TYPE,TIME,CUSTOMER,VEHICLE,DETAILS');
      recentActivity.forEach((activity) => {
        const detail = activity.amount
          ? `$${Math.round(activity.amount).toLocaleString()}`
          : activity.details || activity.messagePreview || '';
        lines.push(
          `${activity.type},${new Date(activity.timestamp).toISOString()},${activity.customer},${activity.vehicle},${detail}`
        );
      });

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `vincentrix-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);

      if (showToast) {
        showToast('✅ Analytics data exported successfully!', 'success');
      }
    } catch (error) {
      console.error('Error exporting analytics:', error);
      if (showToast) {
        showToast('❌ Error exporting analytics data', 'error');
      }
    }
  };

  const dateRanges = ['7days', '30days', '90days'];

  return (
    <div style={{ animation: 'fadeIn 0.4s ease' }}>
      <div
        style={{
          position: 'relative',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
          backdropFilter: 'blur(40px) saturate(150%)',
          WebkitBackdropFilter: 'blur(40px) saturate(150%)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(59, 130, 246, 0.1)',
          padding: '32px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {/* Top highlight */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
        }} />
        
        {/* Inner glow */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.08), transparent 50%)',
          pointerEvents: 'none'
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2
            style={{
              fontSize: '36px',
              fontWeight: '600',
              marginBottom: '12px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              textShadow: '0 0 40px rgba(255, 255, 255, 0.1)'
            }}
          >
            <TrendingUp size={24} color="#3b82f6" />
            Acquisition Analytics & Performance
          </h2>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.5)', 
            fontSize: '15px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
            letterSpacing: '0.5px'
          }}>
            Real-time metrics powered by your live customer data ({rangeLabel})
          </p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
            {dateRanges.map((range) => (
              <button
                key={range}
                onClick={() => setDateRange && setDateRange(range)}
                style={{
                  padding: '8px 16px',
                  background:
                    dateRange === range
                      ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                      : 'rgba(59, 130, 246, 0.1)',
                  border:
                    dateRange === range
                      ? 'none'
                      : '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '12px',
                  color: dateRange === range ? 'white' : theme.text,
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                  boxShadow: dateRange === range ? '0 0 20px rgba(59, 130, 246, 0.4)' : 'none'
                }}
              >
                {getRangeLabel(range)}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={handleExportAnalytics}
          style={{
            position: 'relative',
            zIndex: 1,
            background: 'rgba(16, 185, 129, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '14px',
            padding: '14px 24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#10b981',
            fontSize: '14px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
            e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 0 30px rgba(16, 185, 129, 0.4), 0 4px 15px rgba(16, 185, 129, 0.2)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Download size={18} />
          Export Analytics
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}
      >
        {metricCards.map((metric) => (
          <div
            key={metric.title}
            style={{
              position: 'relative',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
              backdropFilter: 'blur(40px) saturate(150%)',
              WebkitBackdropFilter: 'blur(40px) saturate(150%)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '24px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(59, 130, 246, 0.1)',
              padding: '28px',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              overflow: 'hidden'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
              e.currentTarget.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 100px rgba(59, 130, 246, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(59, 130, 246, 0.1)';
            }}
          >
            {/* Top highlight */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: '10%',
              right: '10%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
            }} />
            
            {/* Inner glow */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.08), transparent 50%)',
              pointerEvents: 'none'
            }} />
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <metric.icon size={20} color={metric.iconColor} />
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: 'rgba(255, 255, 255, 0.4)', 
              marginBottom: '12px', 
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
            }}>
              {metric.title}
            </div>
            <div
              style={{
                fontSize: '48px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #fff 0%, #60a5fa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '12px',
                lineHeight: '1',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                filter: 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.4))',
                position: 'relative',
                zIndex: 1
              }}
            >
              {metric.value}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: theme.textSecondary, 
              marginBottom: '8px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
            }}>
              {metric.subtitle}
            </div>
            <div
              style={{
                padding: '8px 12px',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#3b82f6',
                marginBottom: '8px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
              }}
            >
              {metric.detail}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '11px',
                color: colors.success
              }}
            >
              <ArrowUpRight size={14} />
              {metric.trend}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          ...theme.glass,
          padding: '30px',
          borderRadius: '20px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          marginBottom: '30px'
        }}
      >
        <h3
          style={{
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <BarChart3 size={20} color={colors.primary} />
          7-Day Performance Trends
        </h3>
        {hasTrendData ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '12px', alignItems: 'flex-end', height: '200px' }}>
            {dailyTrends.map((day, index) => {
              const maxCustomers = Math.max(1, ...dailyTrends.map((d) => d.customers));
              const maxOffer = Math.max(1, ...dailyTrends.map((d) => d.offerTotal));
              const customersHeight = (day.customers / maxCustomers) * 100;
              const offerHeight = (day.offerTotal / maxOffer) * 100;
              return (
                <div key={`day-${day.date || index}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', width: '100%' }}>
                    <div
                      style={{
                        width: '100%',
                        background: `linear-gradient(to top, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                        height: `${customersHeight}%`,
                        minHeight: '10px',
                        borderRadius: '8px 8px 0 0',
                        position: 'relative'
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '-20px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '11px',
                          color: theme.text,
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {day.customers}
                      </div>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        background: `linear-gradient(to top, ${colors.success} 0%, #10b981 100%)`,
                        height: `${offerHeight * 0.6}%`,
                        minHeight: '6px',
                        borderRadius: '0 0 8px 8px'
                      }}
                    ></div>
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: theme.text }}>{day.day}</div>
                  <div style={{ fontSize: '10px', color: theme.textSecondary }}>
                    {day.acquisitions} acquired • ${Math.round(day.offerTotal).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: theme.textSecondary, padding: '40px 0' }}>
            Not enough activity yet to display a trend.
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '16px', height: '16px', background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`, borderRadius: '4px' }}></div>
            <span style={{ fontSize: '12px', color: theme.textSecondary }}>New Customers</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '16px', height: '16px', background: colors.success, borderRadius: '4px' }}></div>
            <span style={{ fontSize: '12px', color: theme.textSecondary }}>Offer Value</span>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}
      >
        {[
          {
            title: 'Acquisition Cost',
            value: `$${Math.round(financialStats.acquisitionRevenue * 0.1).toLocaleString()}`,
            subtitle: 'Estimated marketing & sourcing',
            icon: DollarSign,
            iconColor: colors.success,
            color: '#10b981',
            detail: `${financialStats.acquisitionCount} vehicle${financialStats.acquisitionCount === 1 ? '' : 's'} acquired`
          },
          {
            title: 'Trade-In Gross',
            value: `$${Math.round(tradeInTotals.estimatedGross).toLocaleString()}`,
            subtitle: '$1,200 avg per trade',
            icon: TrendingUp,
            iconColor: colors.warning,
            color: '#f59e0b',
            detail: `${tradeInTotals.totalTradeIns} trade-ins captured`
          },
          {
            title: 'Profit per Acquisition',
            value: `$${Math.round(financialStats.acquisitionProfit).toLocaleString()}`,
            subtitle: '$4,300 avg per vehicle',
            icon: DollarSign,
            iconColor: '#8b5cf6',
            color: '#8b5cf6',
            detail: `Across ${financialStats.acquisitionCount} closed deals`
          }
        ].map((metric) => (
          <div
            key={metric.title}
            style={{
              ...theme.glass,
              padding: '24px',
              borderRadius: '16px',
              border: `2px solid ${metric.color}40`,
              transition: 'all 0.3s',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 12px 32px ${metric.color}40`;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
              <metric.icon size={18} color={metric.iconColor} />
            </div>
            <div style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '8px', fontWeight: '500' }}>
              {metric.title}
            </div>
            <div
              style={{
                fontSize: '42px',
                fontWeight: '700',
                color: metric.color,
                marginBottom: '8px',
                lineHeight: '1'
              }}
            >
              {metric.value}
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '8px' }}>
              {metric.subtitle}
            </div>
            <div
              style={{
                padding: '8px 12px',
                background: `${metric.color}15`,
                borderRadius: '8px',
                fontSize: '12px',
                color: metric.color
              }}
            >
              {metric.detail}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          ...theme.glass,
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          marginBottom: '32px'
        }}
      >
        <h3
          style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          Trade-In Performance
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          <div
            style={{
              ...theme.glass,
              padding: '24px',
              borderRadius: '16px',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '48px', fontWeight: '700', color: '#3b82f6', marginBottom: '8px' }}>
              {tradeInTotals.totalTradeIns}
            </div>
            <div style={{ fontSize: '14px', color: theme.textSecondary, marginBottom: '12px' }}>
              Total Trade-Ins Captured
            </div>
            <div style={{ fontSize: '12px', color: theme.text }}>
              AI collected trade info on {tradeInTotals.customersWithTradeIns} customers
            </div>
          </div>
          <div
            style={{
              ...theme.glass,
              padding: '24px',
              borderRadius: '16px',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '48px', fontWeight: '700', color: '#10b981', marginBottom: '8px' }}>
              ${tradeInTotals.estimatedGross.toLocaleString()}
            </div>
            <div style={{ fontSize: '14px', color: theme.textSecondary, marginBottom: '12px' }}>
              Trade-In Gross
            </div>
            <div style={{ fontSize: '12px', color: theme.text }}>$1,200 avg profit per trade</div>
          </div>
          <div
            style={{
              ...theme.glass,
              padding: '24px',
              borderRadius: '16px',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '48px', fontWeight: '700', color: '#8b5cf6', marginBottom: '8px' }}>
              {tradeInTotals.tradeCaptureRate}%
            </div>
            <div style={{ fontSize: '14px', color: theme.textSecondary, marginBottom: '12px' }}>
              Trade Capture Rate
            </div>
            <div style={{ fontSize: '12px', color: theme.text }}>
              {tradeInTotals.customersWithTradeIns} of {metrics.totalCustomers} customers
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          ...theme.glass,
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          marginBottom: '32px'
        }}
      >
        <h3
          style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          Lead Source Breakdown
        </h3>
        {leadSources.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            {leadSources.map((item, index) => {
              const maxCount = Math.max(1, ...leadSources.map((source) => source.count));
              const barWidth = (item.count / maxCount) * 100;
              return (
                <div
                  key={`lead-source-${item.source || index}`}
                  style={{
                    ...theme.glass,
                    padding: '24px',
                    borderRadius: '16px',
                    border: `2px solid ${item.color}40`,
                    transition: 'all 0.3s',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.borderColor = item.color;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.borderColor = `${item.color}40`;
                  }}
                >
                  <div style={{ fontSize: '36px', fontWeight: '700', color: item.color, marginBottom: '4px' }}>
                    {item.count}
                  </div>
                  <div style={{ fontSize: '14px', color: theme.textSecondary, marginBottom: '8px' }}>
                    {item.source}
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '8px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        width: `${barWidth}%`,
                        height: '100%',
                        background: item.color,
                        borderRadius: '4px',
                        transition: 'width 0.3s'
                      }}
                    ></div>
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: item.color }}>
                    {item.percentage}% of opportunities
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: theme.textSecondary }}>No lead source data available.</div>
        )}
      </div>

      <div
        style={{
          ...theme.glass,
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          marginBottom: '32px'
        }}
      >
        <h3
          style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          AI Performance Metrics
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
          {aiPerformanceMetrics.map((metric) => (
            <div
              key={metric.title}
              style={{
                ...theme.glass,
                padding: '24px',
                borderRadius: '12px',
                border: `2px solid ${metric.color}40`,
                textAlign: 'center',
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 12px 32px ${metric.color}40`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ fontSize: '56px', fontWeight: '700', color: metric.color, marginBottom: '8px', lineHeight: '1' }}>
                {metric.value}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>
                {metric.title}
              </div>
              <div style={{ fontSize: '12px', color: theme.textSecondary }}>{metric.description}</div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          ...theme.glass,
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          marginBottom: '32px'
        }}
      >
        <h3
          style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          Acquisition Pipeline Progress
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {pipelineStages.map((stage, index) => (
            <React.Fragment key={stage.label}>
              <div
                style={{
                  flex: 1,
                  minWidth: '200px',
                  ...theme.glass,
                  padding: '24px',
                  borderRadius: '12px',
                  border: `2px solid ${stage.color}40`,
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.borderColor = stage.color;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.borderColor = `${stage.color}40`;
                }}
              >
                <div style={{ fontSize: '48px', fontWeight: '700', color: stage.color, marginBottom: '8px' }}>
                  {stage.value}
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: theme.text, marginBottom: '4px' }}>
                  {stage.label}
                </div>
                <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '8px' }}>
                  {stage.description}
                </div>
                {index < pipelineStages.length - 1 && stage.value > 0 && pipelineStages[index + 1].value > 0 && (
                  <div style={{ padding: '6px 12px', background: `${stage.color}20`, borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: stage.color }}>
                    {Math.round((pipelineStages[index + 1].value / stage.value) * 100)}% →
                  </div>
                )}
              </div>
              {index < pipelineStages.length - 1 && (
                <div style={{ fontSize: '32px', color: colors.primary }}>→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div
        style={{
          ...theme.glass,
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          marginBottom: '32px'
        }}
      >
        <h3
          style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          Team Performance
        </h3>
        {teamPerformance.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(59, 130, 246, 0.2)' }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: theme.textSecondary }}>
                    Name
                  </th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: theme.textSecondary }}>
                    Role
                  </th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: theme.textSecondary }}>
                    Leads
                  </th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: theme.textSecondary }}>
                    Appointments
                  </th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: theme.textSecondary }}>
                    Acquisitions
                  </th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: theme.textSecondary }}>
                    Conversion %
                  </th>
                  <th style={{ padding: '16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: theme.textSecondary }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {teamPerformance.map((member, index) => {
                  const initials = getInitials(member.name);
                  return (
                    <tr
                      key={`team-member-${member.name || index}`}
                      style={{
                        borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
                        transition: 'background 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${colors.primary}40 0%, ${colors.secondary}40 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: '700',
                              color: colors.primary
                            }}
                          >
                            {initials}
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: theme.text }}>{member.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background:
                              member.role === 'Appraiser'
                                ? 'rgba(59, 130, 246, 0.15)'
                                : 'rgba(139, 92, 246, 0.15)',
                            color: member.role === 'Appraiser' ? '#3b82f6' : '#8b5cf6'
                          }}
                        >
                          {member.role}
                        </span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right', fontSize: '14px', color: theme.text }}>
                        {member.leads}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right', fontSize: '14px', color: theme.text }}>
                        {member.appointments}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right', fontSize: '14px', color: theme.text }}>
                        {member.purchases}
                      </td>
                      <td
                        style={{
                          padding: '16px',
                          textAlign: 'right',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: theme.text
                        }}
                      >
                        {member.conversion}%
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background:
                              member.status === 'Active'
                                ? 'rgba(16, 185, 129, 0.15)'
                                : 'rgba(245, 158, 11, 0.15)',
                            color: member.status === 'Active' ? '#10b981' : '#f59e0b'
                          }}
                        >
                          {member.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: theme.textSecondary }}>
            No team assignments yet in this date range.
          </div>
        )}
      </div>

      <div
        style={{
          ...theme.glass,
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          marginBottom: '32px'
        }}
      >
        <h3
          style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          <Activity size={18} color={colors.primary} />
          Recent Activity
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
          {recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <div
                key={`activity-${activity.timestamp || index}`}
                style={{
                  padding: '16px',
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background:
                      activity.type === 'acquisition'
                        ? `linear-gradient(135deg, ${colors.success} 0%, #10b981 100%)`
                        : activity.type === 'appointment'
                        ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                        : activity.type === 'trade-in'
                        ? `linear-gradient(135deg, ${colors.warning} 0%, #f59e0b 100%)`
                        : activity.type === 'conversation'
                        ? `linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)`
                        : `linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px'
                  }}
                >
                  {activity.type === 'acquisition' ? (
                    <Car size={20} color="white" />
                  ) : activity.type === 'appointment' ? (
                    <Calendar size={20} color="white" />
                  ) : activity.type === 'trade-in' ? (
                    <RefreshCw size={20} color="white" />
                  ) : activity.type === 'conversation' ? (
                    <MessageSquare size={20} color="white" />
                  ) : (
                    <Activity size={20} color="white" />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: theme.text }}>{activity.action}</span>
                    <span style={{ fontSize: '12px', color: theme.textSecondary }}>•</span>
                    <span style={{ fontSize: '12px', color: theme.textSecondary }}>
                      {formatRelativeTime(activity.timestamp)}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: theme.text }}>
                    <span style={{ fontWeight: '500' }}>{activity.customer}</span>
                    <span style={{ color: theme.textSecondary }}> — {activity.vehicle}</span>
                    {activity.amount && (
                      <span style={{ color: colors.success, fontWeight: '600', marginLeft: '8px' }}>
                        ${Math.round(activity.amount).toLocaleString()}
                      </span>
                    )}
                    {activity.details && (
                      <span style={{ color: colors.primary, marginLeft: '8px' }}>{activity.details}</span>
                    )}
                    {activity.messagePreview && (
                      <span style={{ color: theme.textSecondary, marginLeft: '8px' }}>
                        “{activity.messagePreview.slice(0, 80)}{activity.messagePreview.length > 80 ? '…' : ''}”
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', color: theme.textSecondary }}>
              No activity recorded in this date range yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

